import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import animationData from "@/data/404-page-animation/404 error page with cat.json";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { notFoundCopy } from "@/lib/siteCopy";

export function NotFoundPage() {
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = notFoundCopy[lang];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <>
      <SEO
        title="404 - Page Not Found | DevDotCom"
        description="The page you're looking for doesn't exist. Let's get you back on track."
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-secondary/10 flex items-center justify-center px-4 py-8 sm:py-16">
        <div className="max-w-4xl w-full text-center">
          <div className="w-full max-w-[250px] sm:max-w-[300px] md:max-w-[350px] mx-auto mb-12 sm:mb-16 animate-in fade-in zoom-in duration-700">
            <Lottie
              animationData={animationData}
              loop={true}
              autoplay={true}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "220px",
              }}
            />
          </div>

          <div className="space-y-4 sm:space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300 pt-8 relative z-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground/90">
              {t.title}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
              {t.body}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mt-6 sm:mt-8 px-4">
              <button
                onClick={() => navigate("/")}
                className="group relative w-full sm:w-auto px-8 sm:px-10 py-3.5 sm:py-4 
                  bg-black/80 backdrop-blur-md text-white 
                  rounded-full font-semibold text-base sm:text-lg 
                  border border-white/20
                  transition-all duration-300 
                  hover:bg-black/90 hover:border-white/40 hover:shadow-2xl hover:shadow-black/20
                  active:scale-95 
                  sm:min-w-[200px]"
              >
                <span className="relative z-10">{t.goHome}</span>
              </button>

              <button
                onClick={() => navigate(-1)}
                className="group relative w-full sm:w-auto px-8 sm:px-10 py-3.5 sm:py-4 
                  bg-white/10 backdrop-blur-md text-foreground 
                  rounded-full font-semibold text-base sm:text-lg 
                  border border-black/10
                  transition-all duration-300 
                  hover:bg-white/20 hover:border-black/20 hover:shadow-xl
                  active:scale-95 
                  sm:min-w-[200px]"
              >
                <span className="relative z-10">{t.goBack}</span>
              </button>
            </div>

            <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-border/50 px-4">
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                {t.explore}
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                <button
                  onClick={() => navigate("/services")}
                  className="px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium text-foreground/80 hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-all duration-200 hover:scale-105"
                >
                  {t.Services}
                </button>
                <button
                  onClick={() => navigate("/products")}
                  className="px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium text-foreground/80 hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-all duration-200 hover:scale-105"
                >
                  {t.Products}
                </button>
                <button
                  onClick={() => navigate("/about")}
                  className="px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium text-foreground/80 hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-all duration-200 hover:scale-105"
                >
                  {t.About}
                </button>
                <button
                  onClick={() => navigate("/contact")}
                  className="px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium text-foreground/80 hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-all duration-200 hover:scale-105"
                >
                  {t.Contact}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
